import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { queryClient } from "@/lib/queryClient";
import { ADMIN_ROUTES } from "@/lib/adminRoutes";
import { ScrollToTop } from "@/components/ScrollToTop";
import { CookieConsentBanner } from "@/components/CookieConsentBanner";
import { useMaintenanceMode } from "@/hooks/useMaintenanceMode";
import { useAuth } from "@/hooks/useAuth";

// Landing loaded eagerly for FCP
import Landing from "@/pages/landing";

// All other pages lazy-loaded for code splitting
const Home = lazy(() => import("@/pages/home"));
const Login = lazy(() => import("@/pages/login"));
const ForgotPassword = lazy(() => import("@/pages/forgot-password"));
const ResetPassword = lazy(() => import("@/pages/reset-password"));
const Analysis = lazy(() => import("@/pages/analysis"));
const CompanyFormation = lazy(() => import("@/pages/company-formation"));
const ServiceLevel2Internal = lazy(() => import("@/pages/service-level2-internal"));
const ServiceLevel3Internal = lazy(() => import("@/pages/service-level3-internal"));
const AidsInternal = lazy(() => import("@/pages/aids-internal"));
const OfficialAidsStatus = lazy(() => import("@/pages/official-aids-status"));
const AidsInfo = lazy(() => import("@/pages/aids-info"));
const AdminLogin = lazy(() => import("@/pages/admin-login"));
const AdminDashboard = lazy(() => import("@/pages/admin-dashboard"));
const AdminUsers = lazy(() => import("@/pages/admin-users"));
const AdminUserDetail = lazy(() => import("@/pages/admin-user-detail"));
const AdminPayments = lazy(() => import("@/pages/admin-payments"));
const AdminInvoices = lazy(() => import("@/pages/admin-invoices"));
const AdminConfig = lazy(() => import("@/pages/admin-config"));
const AdminPaymentConfig = lazy(() => import("@/pages/admin-payment-config"));
const AdminSecurity = lazy(() => import("@/pages/admin-security"));
const AdminMessages = lazy(() => import("@/pages/admin-messages"));
const AdminDemoAccount = lazy(() => import("@/pages/admin-demo-account"));
const AdminProfitability = lazy(() => import("@/pages/admin-profitability"));
const AdminCache = lazy(() => import("@/pages/admin-cache"));
const AdminSeoAudit = lazy(() => import("@/pages/admin-seo-audit"));
const AdminSeoOptimization = lazy(() => import("@/pages/admin-seo-optimization"));
const AdminJobs = lazy(() => import("@/pages/admin-jobs"));
const AdminXPosts = lazy(() => import("@/pages/admin-x-posts"));
const PaymentForm = lazy(() => import("@/pages/payment-form"));
const PaymentSuccess = lazy(() => import("@/pages/payment-success"));
const UserRegistration = lazy(() => import("@/pages/user-registration"));
const ConstitutionInfo = lazy(() => import("@/pages/constitution-info"));
const DueGuide = lazy(() => import("@/pages/due-guide"));
const Sl1EuroGuide = lazy(() => import("@/pages/sl-1-euro-guide"));
const AutonomoSocietarioGuide = lazy(() => import("@/pages/autonomo-societario-guide"));
const EnisaGuide = lazy(() => import("@/pages/enisa-guide"));
const RmcGuide = lazy(() => import("@/pages/rmc-guide"));
const TermsOfService = lazy(() => import("@/pages/terms-of-service"));
const PrivacyPolicy = lazy(() => import("@/pages/privacy-policy"));
const LegalNotice = lazy(() => import("@/pages/legal-notice"));
const VerifactuDeclaration = lazy(() => import("@/pages/verifactu-declaration"));
const AIPolicy = lazy(() => import("@/pages/ai-policy"));

const CrearEmpresaOnlineLanding = lazy(() => import("@/pages/landings/crear-empresa-online"));
const AyudasEnisaLanding = lazy(() => import("@/pages/landings/ayudas-enisa"));
const FinanciacionStartupsLanding = lazy(() => import("@/pages/landings/financiacion-startups"));

const HelpCenter = lazy(() => import("@/pages/help-center"));
const Contact = lazy(() => import("@/pages/contact"));
const SharedReport = lazy(() => import("@/pages/shared-report"));
const NotFound = lazy(() => import("@/pages/not-found"));
const Maintenance = lazy(() => import("@/pages/maintenance"));

function MaintenanceGuard({ children }: { children: React.ReactNode }) {
  const { isMaintenanceMode, maintenanceMessage } = useMaintenanceMode();
  const { isAdmin, isAuthenticated } = useAuth();
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith("/cx9m3k");

  // Solo aplica a usuarios autenticados no-admin; la landing y zonas públicas quedan intactas.
  if (isMaintenanceMode && isAuthenticated && !isAdmin && !isAdminRoute) {
    return <Maintenance message={maintenanceMessage} />;
  }

  return <>{children}</>;
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <ScrollToTop />
        <Suspense fallback={<div className="min-h-screen flex items-center justify-center bg-background"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" role="status" aria-label="Cargando"><span className="sr-only">Cargando...</span></div></div>}>
          <MaintenanceGuard>
            <Routes>
              <Route path="/" element={<Landing />} />
              <Route path="/home" element={<Home />} />
              <Route path="/login" element={<Login />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/analysis" element={<Analysis />} />
              <Route path="/analysis/:id" element={<Analysis />} />
              
              <Route path="/service-level1" element={<Analysis />} />
              <Route path="/service-level2-internal" element={<ServiceLevel2Internal />} />
              <Route path="/service-level3-internal" element={<ServiceLevel3Internal />} />
              
              <Route path="/company-formation" element={<ServiceLevel2Internal />} />
              <Route path="/company-formation/:projectId" element={<CompanyFormation />} />
              
              <Route path="/aids" element={<ServiceLevel3Internal />} />
              <Route path="/aids-internal" element={<AidsInternal />} />
              <Route path="/official-aids-status" element={<OfficialAidsStatus />} />
              <Route path="/aids-info" element={<AidsInfo />} />
              
              <Route path={ADMIN_ROUTES.login} element={<AdminLogin />} />
              <Route path={ADMIN_ROUTES.dashboard} element={<AdminDashboard />} />
              <Route path={ADMIN_ROUTES.users} element={<AdminUsers />} />
              <Route path={ADMIN_ROUTES.userDetail} element={<AdminUserDetail />} />
              <Route path={ADMIN_ROUTES.payments} element={<AdminPayments />} />
              <Route path={ADMIN_ROUTES.invoices} element={<AdminInvoices />} />
              <Route path={ADMIN_ROUTES.config} element={<AdminConfig />} />
              <Route path={ADMIN_ROUTES.paymentConfig} element={<AdminPaymentConfig />} />
              <Route path={ADMIN_ROUTES.security} element={<AdminSecurity />} />
              <Route path={ADMIN_ROUTES.messages} element={<AdminMessages />} />
              <Route path={ADMIN_ROUTES.officialAids} element={<OfficialAidsStatus />} />
              <Route path={ADMIN_ROUTES.demoAccount} element={<AdminDemoAccount />} />
              <Route path={ADMIN_ROUTES.profitability} element={<AdminProfitability />} />
              <Route path={ADMIN_ROUTES.cache} element={<AdminCache />} />
              <Route path={ADMIN_ROUTES.seoAudit} element={<AdminSeoAudit />} />
              <Route path={ADMIN_ROUTES.seoOptimization} element={<AdminSeoOptimization />} />
              <Route path={ADMIN_ROUTES.jobs} element={<AdminJobs />} />
              <Route path={ADMIN_ROUTES.xPosts} element={<AdminXPosts />} />
              
              <Route path="/payment-form" element={<PaymentForm />} />
              <Route path="/payment-success" element={<PaymentSuccess />} />
              <Route path="/user-registration" element={<UserRegistration />} />
              
              <Route path="/constitution-info" element={<ConstitutionInfo />} />
<Route path="/due-guide" element={<DueGuide />} />
              <Route path="/sl-1-euro" element={<Sl1EuroGuide />} />
              <Route path="/autonomo-societario" element={<AutonomoSocietarioGuide />} />
              <Route path="/enisa-guide" element={<EnisaGuide />} />
              <Route path="/rmc-guide" element={<RmcGuide />} />
              <Route path="/terms-of-service" element={<TermsOfService />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/legal-notice" element={<LegalNotice />} />
              <Route path="/declaracion-verifactu" element={<VerifactuDeclaration />} />
              <Route path="/politica-ia" element={<AIPolicy />} />
              
              {/* SEO landings (Spanish slugs for Google Ads campaigns) */}
              <Route path="/crear-empresa-online" element={<CrearEmpresaOnlineLanding />} />
              <Route path="/ayudas-enisa" element={<AyudasEnisaLanding />} />
              <Route path="/financiacion-startups" element={<FinanciacionStartupsLanding />} />

              <Route path="/help-center" element={<HelpCenter />} />
              <Route path="/contact" element={<Contact />} />
              
              <Route path="/report/:token" element={<SharedReport />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </MaintenanceGuard>
        </Suspense>
        <CookieConsentBanner />
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
